import { Box, Checkbox, Chip, CircularProgress, Divider, Typography } from '@mui/material'
import { FunctionComponent, useEffect, useState } from 'react'
import { DialogWrapper } from '../../../presentation-components/display-elements-components/dialog-wrapper'
import ContactSupplierService from '../../../../shared/services/contact-supplier/contact-supplier.service'
import {
  ILookupActivityPreview,
  ILookupActivitySuggestion,
  LookupActivityConfidence,
} from '../../../../shared/services/contact-supplier/contact-supplier.model'

// Cap how many rows we render so a very large unclassified set stays responsive; the honest total
// is always shown so a capped list never reads as "that's everything".
const MAX_RENDERED = 300

const confidenceChip: Record<LookupActivityConfidence, { label: string, color: 'success' | 'warning' | 'default' | 'info' }> = {
  HIGH: { label: 'High', color: 'success' },
  MEDIUM: { label: 'Medium', color: 'warning' },
  REVIEW: { label: 'Review', color: 'info' },
  NONE: { label: 'No match', color: 'default' },
}

export const LookUpDefaultActivityDialog: FunctionComponent<{
  open: boolean,
  onClose: () => void,
  supplierIds?: string[],
  onApplied: (result: { applied: number, skipped: number }) => void,
}> = ({ open, onClose, supplierIds, onApplied }) => {
  const contactSupplierService = ContactSupplierService.getInstance()

  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [preview, setPreview] = useState<ILookupActivityPreview | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (!open) return
    setIsLoading(true)
    setPreview(null)
    setSelectedIds(new Set())
    contactSupplierService.previewLookupDefaultActivity(supplierIds)
      .then(response => {
        setPreview(response)
        // Pre-tick confident matches (High/Medium); leave Review rows for the user to opt in.
        const preTicked = response.suggestions
          .filter(s => s.matched && (s.confidence === 'HIGH' || s.confidence === 'MEDIUM'))
          .map(s => s.supplierId)
        setSelectedIds(new Set(preTicked))
      })
      .catch(() => setPreview(null))
      .finally(() => setIsLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, supplierIds])

  const matched: ILookupActivitySuggestion[] = preview ? preview.suggestions.filter(s => s.matched) : []
  const unmatchedCount = preview ? preview.total - matched.length : 0
  const rendered = matched.slice(0, MAX_RENDERED)

  const toggle = (supplierId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(supplierId)) next.delete(supplierId)
      else next.add(supplierId)
      return next
    })
  }

  const submitHandler = () => {
    if (!preview || selectedIds.size === 0) return
    const items = matched
      .filter(s => selectedIds.has(s.supplierId) && s.spendBasedConversionId)
      .map(s => ({ supplierId: s.supplierId, spendBasedConversionId: s.spendBasedConversionId as string, companyHouseNumber: s.matchedCompanyNumber }))
    setIsSubmitting(true)
    contactSupplierService.applyLookupDefaultActivity(items)
      .then(result => {
        onApplied(result)
        onClose()
      })
      .catch(() => { /* handled upstream via thrown error logging */ })
      .finally(() => setIsSubmitting(false))
  }

  return (
    <DialogWrapper
      open={open}
      onClose={onClose}
      title="Look up default activity"
      cancelHandler={onClose}
      submitHandler={submitHandler}
      isSubmitting={isSubmitting}
      disableSaveButton={isLoading || !preview || !preview.sourceIsDefra || selectedIds.size === 0}
      maxWidth="sm"
      submitButtonText={selectedIds.size > 0 ? `Apply ${selectedIds.size} selected` : 'Apply'}
      submitButtonLoadingText="Applying"
    >
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!isLoading && preview && !preview.sourceIsDefra && (
        <Typography variant="body2" sx={{ py: 2 }}>
          Automatic lookup uses UK Companies House data and is available for organisations using the
          DEFRA spend-based source. Your current spend-based source is different, so suggestions are
          not available here. You can still set a default activity manually on each supplier.
        </Typography>
      )}

      {!isLoading && preview && preview.sourceIsDefra && (
        <Box>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Matched <strong>{matched.length}</strong> of <strong>{preview.total}</strong> supplier{preview.total === 1 ? '' : 's'} with no default activity.
            Tick the ones to apply; nothing changes until you confirm.
          </Typography>
          {matched.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No confident matches were found. These suppliers are left unchanged.
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {rendered.map(s => (
              <Box key={s.supplierId}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', py: 0.75 }}>
                  <Checkbox
                    size="small"
                    checked={selectedIds.has(s.supplierId)}
                    onChange={() => toggle(s.supplierId)}
                    sx={{ pt: 0.25 }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.supplierName}</Typography>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={confidenceChip[s.confidence].label}
                        color={confidenceChip[s.confidence].color}
                        sx={{ height: 20 }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {s.suggestedIdentifier ? `Code ${s.suggestedIdentifier}: ` : ''}{s.suggestedActivity}
                    </Typography>
                    {s.matchedCompanyName && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Matched to {s.matchedCompanyName}{s.matchedCompanyNumber ? ` (Companies House ${s.matchedCompanyNumber})` : ''}{s.candidateCount > 1 ? `, ${s.candidateCount} similarly named companies` : ''}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Divider />
              </Box>
            ))}
          </Box>
          {matched.length > MAX_RENDERED && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Showing the first {MAX_RENDERED} of {matched.length} matches. Apply these, then run the lookup again for the rest.
            </Typography>
          )}
          {unmatchedCount > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {unmatchedCount} supplier{unmatchedCount === 1 ? '' : 's'} had no confident match and will be left unchanged.
            </Typography>
          )}
        </Box>
      )}
    </DialogWrapper>
  )
}
